import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const RESIZE_DEBOUNCE_MS = 150;

export class BlogHorizontalScroll {
  private section: HTMLElement;
  private pinWrap: HTMLElement;
  private tween: gsap.core.Tween | null = null;
  private resizeListener: () => void;
  private resizeDebounceId: number | null = null;
  private setupRetryCount = 0;
  private readonly maxSetupRetries = 5;

  constructor(section: HTMLElement, pinWrap: HTMLElement) {
    this.section = section;
    this.pinWrap = pinWrap;
    this.resizeListener = () => this.onResize();
    window.addEventListener('resize', this.resizeListener);
    // Run after layout is ready (images may still be loading)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.setup());
    });
  }

  private getScrollDistance(): number {
    const sectionWidth = this.section.offsetWidth;
    const contentWidth = this.pinWrap.scrollWidth;
    return Math.max(0, contentWidth - sectionWidth);
  }

  private setup(): void {
    if (this.tween) {
      this.tween.kill();
      const st = this.tween.scrollTrigger;
      if (st) st.kill();
      this.tween = null;
    }

    const distance = this.getScrollDistance();

    if (distance <= 0) {
      if (this.setupRetryCount < this.maxSetupRetries) {
        this.setupRetryCount++;
        setTimeout(() => this.setup(), 200 * this.setupRetryCount);
      }
      return;
    }

    gsap.set(this.pinWrap, { x: 0, force3D: true });

    this.tween = gsap.to(this.pinWrap, {
      x: -distance,
      ease: 'none',
      force3D: true,
      overwrite: true,
      scrollTrigger: {
        trigger: this.section,
        start: 'top top',
        end: `+=${distance}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.8,
        invalidateOnRefresh: true,
      },
    });

    ScrollTrigger.refresh();
  }

  private onResize(): void {
    if (this.resizeDebounceId !== null) {
      clearTimeout(this.resizeDebounceId);
    }
    this.resizeDebounceId = window.setTimeout(() => {
      this.resizeDebounceId = null;
      this.setupRetryCount = 0;
      this.setup();
    }, RESIZE_DEBOUNCE_MS);
  }

  public dispose(): void {
    window.removeEventListener('resize', this.resizeListener);
    if (this.resizeDebounceId !== null) {
      clearTimeout(this.resizeDebounceId);
      this.resizeDebounceId = null;
    }
    if (this.tween) {
      const st = this.tween.scrollTrigger;
      if (st) st.kill();
      this.tween.kill();
      this.tween = null;
    }
    gsap.set(this.pinWrap, { clearProps: 'transform' });
  }
}
